import axios from 'axios';
import type { Method, AxiosRequestConfig } from 'axios';

const BASE_URL = process.env.VUE_APP_API_SERVER_URL ?? '';
const API_VERSION = process.env.VUE_APP_API_VERSION || 'v1';

let authToken: string | null = null;
let clientId: string | null = null;

export function setAuthToken(token: string | null) {
    authToken = token;
}

export function getAuthToken() {
    return authToken;
}

export function setClientId(id: string | null) {
    clientId = id;
}

export function getClientId() {
    return clientId;
}

const axiosInstance = axios.create({ baseURL: BASE_URL });

axiosInstance.interceptors.request.use((config) => {
    config.headers['Content-Type'] = 'application/json';
    config.headers['X-Version'] = API_VERSION;
    if (authToken) {
        config.headers['Authorization'] = `Bearer ${authToken}`;
    }
    if (clientId) {
        config.headers['X-Client-Id'] = clientId;
    }
    return config;
});

// 401 interceptor — refresh token and retry the original request once.
// Uses a shared in-flight promise so concurrent 401s only trigger one refresh.
// Guards against refresh failures: Authentication.refreshToken() swallows errors
// and redirects to login, so we detect failure by checking whether the token
// actually changed after the call returns.
let refreshInFlight: Promise<void> | null = null;

axiosInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
        const config = error.config as AxiosRequestConfig & { _retry?: boolean };

        // Don't intercept the refresh call itself, or any already-retried request.
        if (
            error.response?.status !== 401 ||
            config._retry ||
            config.url?.includes('auth/token/refresh')
        ) {
            return Promise.reject(error);
        }

        config._retry = true;
        const tokenBefore = authToken;

        // Import lazily to avoid circular dependency (authentication imports client).
        if (!refreshInFlight) {
            const { default: Authentication } = await import('@/plugins/authentication');
            refreshInFlight = Authentication.refreshToken(false).finally(() => {
                refreshInFlight = null;
            });
        }

        await refreshInFlight;

        // If the token didn't change, the refresh failed.
        // Authentication.refreshToken() already called redirectToLogin() — just reject.
        if (!authToken || authToken === tokenBefore) {
            return Promise.reject(error);
        }

        // Refresh succeeded — retry with the new token.
        (config.headers as Record<string, string>).Authorization = `Bearer ${authToken}`;
        return axiosInstance(config);
    }
);

async function request<T>(method: Method, url: string, data?: object): Promise<T> {
    const response = await axiosInstance.request<T>({ method, url, data });
    return response.data;
}

export const apiClient = {
    get: <T>(url: string) => request<T>('get', url),
    post: <T>(url: string, data?: object) => request<T>('post', url, data),
    put: <T>(url: string, data?: object) => request<T>('put', url, data),
    patch: <T>(url: string, data?: object) => request<T>('patch', url, data),
    delete: <T>(url: string) => request<T>('delete', url),
};
